import * as Sentry from "@sentry/react";
import { parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { useGlobalLoading } from "../components/loading/global-loading-context";
import { AddEventSheet } from "../components/track/AddEvent";
import { ComingUpFeed } from "../components/track/ComingUpFeed";
import { CurrentStepCard } from "../components/track/CurrentStepCard";
import { EventDetailSheet } from "../components/track/EventDetailSheet";
import { TrackOverview } from "../components/track/TrackOverview";
import { TrackSkeleton } from "../components/track/TrackSkeleton";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { useTrackEvents } from "../hooks/track/useTrackEvents";
import { useTwoWeekWindow } from "../hooks/track/useTwoWeekWindow";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import {
	TRACK_DAY_TAPPED,
	TRACK_EVENT_OPENED,
	TRACK_PAGE_VIEWED,
} from "../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../lib/track/parseSyntheticEventId";
import type { Event } from "../types/event";
import styles from "./TrackPage.module.css";

export function TrackPage() {
	const { t } = useClientTranslations("track");
	const { logEvent } = useAnalytics();
	const { setLoading } = useGlobalLoading();

	// Phase 38 D-10 / D-13 — read ?day= query param (tolerant parse).
	const [searchParams, setSearchParams] = useSearchParams();
	const dayParam = searchParams.get("day");

	// D-13: tolerant parse — bad input returns undefined (silent fallback).
	const anchor = useMemo<Date | undefined>(() => {
		if (!dayParam || !/^\d{4}-\d{2}-\d{2}$/.test(dayParam)) return undefined;
		const d = parseISO(dayParam);
		return Number.isNaN(d.getTime()) ? undefined : d;
	}, [dayParam]);

	const window = useTwoWeekWindow(anchor);
	const eventsQuery = useTrackEvents(window.from, window.to);

	// Show the global loader during inter-month refetches (prev/next paging).
	// Initial load is covered by <TrackSkeleton /> so we exclude isLoading.
	useEffect(() => {
		const refetching = eventsQuery.isFetching && !eventsQuery.isLoading;
		setLoading(refetching);
		return () => setLoading(false);
	}, [eventsQuery.isFetching, eventsQuery.isLoading, setLoading]);

	const [sheetOpen, setSheetOpen] = useState(false);
	const [sheetDayEvents, setSheetDayEvents] = useState<Event[]>([]);
	const [sheetIndex, setSheetIndex] = useState(0);

	// Phase 37 D-15 / D-16 — TrackPage owns AddEventSheet open state.
	// editingEvent === null → create mode (chooser-first); non-null → edit mode (D-18).
	const [isAddEventOpen, setIsAddEventOpen] = useState(false);
	const [editingEvent, setEditingEvent] = useState<Event | null>(null);

	// INTG-03: track_page_viewed once on mount (CLAUDE.md useEffect ref pattern)
	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEvent(TRACK_PAGE_VIEWED);
		}
	}, [logEvent]);

	// Phase 38 D-12 — pulse target on deep-link land. Null = no pulse.
	const [focusDayIso, setFocusDayIso] = useState<string | null>(null);

	// D-13 + D-12 — consume ?day= on every appearance (notification click can fire
	// multiple times in the same TrackPage mount; each click should re-anchor and
	// pulse). The effect's `[dayParam, anchor, setSearchParams]` deps already gate
	// it correctly: it fires each time dayParam transitions from null → present,
	// and after the setSearchParams clear it goes back to null without re-firing
	// until the next deep-link arrives. React Strict Mode's dev-only double-invoke
	// is idempotent here (clearing already-empty params is a no-op; setting
	// focusDayIso to the same value is a no-op).
	useEffect(() => {
		if (!dayParam) return;
		if (anchor) {
			setFocusDayIso(dayParam);
		} else {
			// Bad ?day — silent fallback + observability breadcrumb (D-13).
			// Date strings are NOT in PHI denylist (RESEARCH Q6).
			Sentry.addBreadcrumb({
				category: "navigation",
				message: "Invalid ?day= param on /track",
				level: "info",
				data: { rawDay: dayParam },
			});
		}
		// Clear ONLY the `day` param so refresh does NOT re-anchor (D-10 / D-11
		// / Specifics §6). Preserves any other query params /track may carry now
		// or in the future (analytics tracking, A/B test buckets, referral
		// sources, etc.). PR review (Copilot, Phase 38 PR #103) caught that
		// `setSearchParams({}, ...)` would clobber unrelated params too.
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("day");
				return next;
			},
			{ replace: true },
		);
	}, [dayParam, anchor, setSearchParams]);

	// Auto-clear pulse after ~1.6s so a re-render doesn't replay it (D-12).
	useEffect(() => {
		if (focusDayIso === null) return;
		const t = setTimeout(() => setFocusDayIso(null), 1600);
		return () => clearTimeout(t);
	}, [focusDayIso]);

	const fireEventOpened = useCallback(
		(event: Event) => {
			// WR-07: enums-only payload — eventId removed to align with the
			// TRACK_EVENT_CREATED|EDITED|DELETED contract (Phase 37 PHI invariant).
			const synthetic = parseSyntheticEventId(event.id);
			logEvent(TRACK_EVENT_OPENED, undefined, {
				type: event.type,
				isRecurring: synthetic !== null,
			});
		},
		[logEvent],
	);

	const handleDayTap = useCallback(
		(dayIso: string, eventsForDay: Event[]) => {
			logEvent(TRACK_DAY_TAPPED, undefined, {
				dayIso,
				eventCount: eventsForDay.length,
			});
			setSheetDayEvents(eventsForDay);
			setSheetIndex(0);
			setSheetOpen(true);
			if (eventsForDay[0]) fireEventOpened(eventsForDay[0]);
		},
		[logEvent, fireEventOpened],
	);

	const handleComingUpClick = useCallback(
		(event: Event) => {
			const dayIso = event.dateTime.slice(0, 10);
			const sameDay = (eventsQuery.data ?? []).filter(
				(e) => e.dateTime.slice(0, 10) === dayIso,
			);
			const idx = sameDay.findIndex((e) => e.id === event.id);
			setSheetDayEvents(sameDay);
			setSheetIndex(Math.max(0, idx));
			setSheetOpen(true);
			fireEventOpened(event);
		},
		[eventsQuery.data, fireEventOpened],
	);

	const handleSheetIndexChange = useCallback(
		(newIndex: number) => {
			setSheetIndex(newIndex);
			const target = sheetDayEvents[newIndex];
			if (target) fireEventOpened(target);
		},
		[sheetDayEvents, fireEventOpened],
	);

	const handleSheetClose = useCallback(() => setSheetOpen(false), []);

	// Phase 37 ADD-01 / D-16 — (+) buttons in TrackOverview + ComingUpFeed
	// open AddEventSheet in create mode (chooser first).
	const handleAddEventOpen = useCallback(() => {
		setEditingEvent(null);
		setIsAddEventOpen(true);
	}, []);

	// Phase 37 EDM-04 / D-18 — Edit click in EventDetailSheet closes the detail
	// sheet and opens AddEventSheet in edit mode (skips chooser, pre-fills form).
	// All three setters run in the same React batch — single render, no flicker
	// (T-37-EDIT-FLOW-RACE mitigation).
	const handleEditFromDetail = useCallback((event: Event) => {
		setSheetOpen(false);
		setEditingEvent(event);
		setIsAddEventOpen(true);
	}, []);

	// Phase 37 D-15 — closing AddEventSheet (Cancel, Save success, X) clears
	// editingEvent so the next open defaults back to create mode.
	const handleAddEventClose = useCallback(() => {
		setIsAddEventOpen(false);
		setEditingEvent(null);
	}, []);

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{eventsQuery.isLoading && <TrackSkeleton />}
				{eventsQuery.isError && (
					<FullPageErrorState
						titleKey="error.title"
						descriptionKey="error.description"
						retryKey="error.retry"
						onRetry={() => eventsQuery.refetch()}
						t={t}
					/>
				)}
				{!eventsQuery.isLoading && !eventsQuery.isError && (
					<>
						<CurrentStepCard />
						<TrackOverview
							events={eventsQuery.data}
							window={window}
							onDayTap={handleDayTap}
							onAddEvent={handleAddEventOpen}
							focusDayIso={focusDayIso}
						/>
						<ComingUpFeed
							events={eventsQuery.data}
							isLoading={false}
							onEventClick={handleComingUpClick}
							onAddEvent={handleAddEventOpen}
						/>
					</>
				)}
			</main>
			<EventDetailSheet
				isOpen={sheetOpen}
				onClose={handleSheetClose}
				events={sheetDayEvents}
				activeIndex={sheetIndex}
				onIndexChange={handleSheetIndexChange}
				onEdit={handleEditFromDetail}
			/>
			<AddEventSheet
				isOpen={isAddEventOpen}
				onClose={handleAddEventClose}
				initialMode={editingEvent ? "edit" : "create"}
				initialType={editingEvent?.type}
				initialValues={editingEvent ?? undefined}
			/>
		</div>
	);
}
